import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ServerPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerAccountDetailsTypes, customerActivityTypes } from 'constants/customer-account.constants';
import { getBankAccounts, getBankAccountsPagination } from 'state/reducers/customer-accounts.reducers';
import { getCompanyBankAccounts } from 'services/customer-accounts.service';
import { pagesEnum } from 'constants/ui.constants';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';

type IProps = {
	button: any;
};

const INITIAL_SORT_BY = 'accountOwnerName';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;

export const BankAccountsTable: React.FC<IProps> = ({ button }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { id } = useParams();
	const bankAccounts = useAppSelector(getBankAccounts);
	const pagination = useAppSelector(getBankAccountsPagination);
	const sortFieldMap: Record<string, string> = {
		accountOwnerName: 'accountOwnerName',
		bankBranch: 'bankBranch',
		accountNumber: 'accountNumber',
		statusHebrew: 'status',
		reference: 'reference',
	};
	const mapSortBy = (sortBy: string) => sortFieldMap[sortBy] || 'accountOwnerName';

	const fetchBankAccounts = useCallback(
		async (page: number, sortOverride: { sortBy: string; sortOrder: Order }) => {
			if (!id) return;
			await dispatch(
				getCompanyBankAccounts(parseInt(id), {
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: mapSortBy(sortOverride.sortBy),
					sortOrder: sortOverride.sortOrder,
				})
			);
		},
		[id, dispatch]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination({
		initialSortBy: INITIAL_SORT_BY,
		initialSortOrder: INITIAL_SORT_ORDER,
		fetchPage: fetchBankAccounts,
	});

	useEffect(() => {
		if (!id) return;
		runFetch(0);
	}, [id, runFetch]);

	const handleSortChangeWithState = useCallback(
		(nextSortBy: string, nextSortOrder: Order) => handleSortChange(nextSortBy, nextSortOrder),
		[handleSortChange]
	);

	return (
		<Stack spacing={2}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
				<Box></Box>
				{button}
			</Box>
			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{pagination.total + ' ' + t('messages.results')}
			</Typography>
			<ServerPaginatedTable
				rowAction={(row) => {
					navigate(
						`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${customerAccountDetailsTypes.BANK_ACCOUNTS}/${row.id}`
					);
				}}
				defaultSortBy={INITIAL_SORT_BY}
				defaultOrderBy={INITIAL_SORT_ORDER}
				data={TableConfig.tableData(bankAccounts)}
				columns={TableConfig.buildColumns(customerActivityTypes.BANK_ACCOUNTS, t)}
				perPage={DEFAULT_PAGE_SIZE}
				serverPagination={{
					total: pagination.total,
					hasMore: pagination.hasMore,
					currentPage,
					onPageChange: handlePageChange,
					onSortChange: handleSortChangeWithState,
					isLoading,
				}}
			/>
		</Stack>
	);
};
